// Small format helpers scoped to the Global Dashboard Overview redesign.
// Inlined here to avoid scattering one-off utilities across the codebase.

// Human-readable duration. Picks the two largest units that fit so the
// number stays compact ("3mo 12d", "7h 4m"). 1 month is approximated as
// 30 days — exact calendar math isn't meaningful for MTBF/MTTR averages.
export const humanizeDuration = (hours, t) => {
  const u = (key, fallback) => (t ? t(`shared.${key}`, fallback) : fallback);
  const min = u('minute_short', 'm');
  const hr = u('hour_short', 'h');
  const day = u('day_short', 'd');
  const mo = u('month_short', 'mo');
  if (hours == null || Number.isNaN(hours)) return '—';
  if (hours <= 0) return `0${min}`;
  const totalMinutes = Math.round(hours * 60);
  if (totalMinutes < 60) return `${totalMinutes}${min}`;
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalHours < 24) return `${totalHours}${hr} ${minutes}${min}`;
  const totalDays = Math.floor(totalHours / 24);
  const remHours = totalHours % 24;
  if (totalDays < 30) return `${totalDays}${day} ${remHours}${hr}`;
  const months = Math.floor(totalDays / 30);
  const remDays = totalDays % 30;
  return `${months}${mo} ${remDays}${day}`;
};

// DD/MM/YYYY HH:mm formatter for "Last updated" labels.
// Per CLAUDE.md rule #7 — never US-format.
// Handles Jackson's LocalDateTime array form ([y, m, d, h, m, s, nano])
// in addition to Date instances and ISO strings.
export const formatDateTime = (value) => {
  if (!value) return '—';
  let d;
  if (Array.isArray(value)) {
    const [y, mo = 1, dy = 1, h = 0, mi = 0, s = 0] = value;
    d = new Date(y, mo - 1, dy, h, mi, s);
  } else if (value instanceof Date) {
    d = value;
  } else {
    d = new Date(value);
  }
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Compute % change between two values. Returns null when prior is missing
// or zero — UI renders null as "no arrow".
export const percentDelta = (current, previous) => {
  if (previous == null || previous === 0) return null;
  if (current == null) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

// safe-divide for ratios — returns null instead of NaN/Infinity so the UI
// can render "—" rather than a nonsensical percentage.
export const pct = (numerator, denominator) => {
  if (!denominator) return null;
  return (numerator / denominator) * 100;
};
